import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Settings, Clock, RefreshCw, Play, Pause, CheckCircle, XCircle,
  Bell, Wifi, Activity, FileText, CreditCard, Save, RotateCcw,
  Info, AlertTriangle, Zap, History, Tag, GitBranch, Calendar,
  ChevronDown, ChevronRight, Bug, Sparkles, Wrench, Rocket,
  Key, Shield, Copy, Eye, EyeOff, Plus, Trash2, Globe, Code,
  DollarSign, CalendarClock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SystemSettings = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('schedulers');
  const [schedulers, setSchedulers] = useState({});
  const [version, setVersion] = useState(null);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expandedVersion, setExpandedVersion] = useState(null);
  
  // PPPoE Webhook States
  const [webhookSettings, setWebhookSettings] = useState({
    api_key: '',
    ip_whitelist_enabled: false,
    allowed_ips: [],
    scheduler_enabled: true,
    scheduler_interval_minutes: 5,
    custom_webhook_url: ''
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [newIp, setNewIp] = useState('');
  const [mikrotikScript, setMikrotikScript] = useState(null);
  const [savingWebhook, setSavingWebhook] = useState(false);
  
  // Billing Settings States
  const [billingSettings, setBillingSettings] = useState({
    extend_method: 'from_due_date',
    invoice_pregen_days: 5
  });
  const [billingOptions, setBillingOptions] = useState([]);
  const [savingBilling, setSavingBilling] = useState(false);

  useEffect(() => {
    fetchData();
    fetchWebhookSettings();
    fetchBillingSettings();
  }, [token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      // Use V2 MySQL endpoint for schedulers
      const [schedulerRes, versionRes] = await Promise.all([
        axios.get(`${API_URL}/api/v2/system/schedulers`, { headers }),
        axios.get(`${API_URL}/api/system/version`, { headers })
      ]);
      
      if (schedulerRes.data.success) {
        setSchedulers(schedulerRes.data.data);
      }
      
      if (versionRes.data.success) {
        setVersion(versionRes.data.data.version);
        setChangelog(versionRes.data.data.changelog);
        // Expand first version by default
        if (versionRes.data.data.changelog.length > 0) {
          setExpandedVersion(versionRes.data.data.changelog[0].version);
        }
      }
    } catch (error) {
      console.error('Error fetching system settings:', error);
      toast.error('Gagal memuat pengaturan sistem');
    } finally {
      setLoading(false);
    }
  };

  // PPPoE Webhook Functions
  const fetchWebhookSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mikrotik/pppoe-webhook-settings`);
      if (response.data.success && response.data.data) {
        setWebhookSettings({
          api_key: response.data.data.api_key || '',
          ip_whitelist_enabled: response.data.data.ip_whitelist_enabled || false,
          allowed_ips: response.data.data.allowed_ips || [],
          scheduler_enabled: response.data.data.scheduler_enabled !== false,
          scheduler_interval_minutes: response.data.data.scheduler_interval_minutes || 5,
          custom_webhook_url: response.data.data.custom_webhook_url || ''
        });
      }
      
      // Fetch MikroTik script template
      const scriptRes = await axios.get(`${API_URL}/api/mikrotik/mikrotik-script-template`);
      if (scriptRes.data.success) {
        setMikrotikScript(scriptRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching webhook settings:', error);
    }
  };

  const generateNewApiKey = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/mikrotik/pppoe-webhook-settings/generate-key`);
      if (response.data.success) {
        setWebhookSettings(prev => ({ ...prev, api_key: response.data.api_key }));
        toast.success('API Key baru berhasil di-generate');
        fetchWebhookSettings(); // Refresh script template with new key
      }
    } catch (error) {
      toast.error('Gagal generate API Key');
    }
  };

  const saveWebhookSettings = async () => {
    try {
      setSavingWebhook(true);
      const response = await axios.post(`${API_URL}/api/mikrotik/pppoe-webhook-settings`, webhookSettings);
      if (response.data.success) {
        toast.success('Pengaturan webhook berhasil disimpan');
        fetchWebhookSettings();
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSavingWebhook(false);
    }
  };

  // Billing Settings Functions
  const fetchBillingSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/billing-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setBillingSettings({
          extend_method: response.data.data.extend_method || 'from_due_date',
          invoice_pregen_days: response.data.data.invoice_pregen_days ?? 5
        });
        setBillingOptions(response.data.data.extend_method_options || []);
      }
    } catch (error) {
      console.error('Error fetching billing settings:', error);
    }
  };

  const saveBillingSettings = async () => {
    try {
      setSavingBilling(true);
      const response = await axios.put(`${API_URL}/api/v2/billing-settings`, billingSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Pengaturan billing berhasil disimpan');
        fetchBillingSettings();
      }
    } catch (error) {
      toast.error('Gagal menyimpan pengaturan billing');
    } finally {
      setSavingBilling(false);
    }
  };

  const addIpToWhitelist = () => {
    if (!newIp.trim()) return;
    // Basic IP validation
    const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
    if (!ipRegex.test(newIp.trim())) {
      toast.error('Format IP tidak valid');
      return;
    }
    if (webhookSettings.allowed_ips.includes(newIp.trim())) {
      toast.error('IP sudah ada dalam daftar');
      return;
    }
    setWebhookSettings(prev => ({
      ...prev,
      allowed_ips: [...prev.allowed_ips, newIp.trim()]
    }));
    setNewIp('');
  };

  const removeIpFromWhitelist = (ip) => {
    setWebhookSettings(prev => ({
      ...prev,
      allowed_ips: prev.allowed_ips.filter(i => i !== ip)
    }));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} berhasil disalin`);
  };

  const updateScheduler = async (schedulerName, updates) => {
    try {
      setSaving(prev => ({ ...prev, [schedulerName]: true }));
      
      // Use V2 MySQL endpoint
      const response = await axios.put(
        `${API_URL}/api/v2/system/schedulers/${schedulerName}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${schedulers[schedulerName]?.name || schedulerName} berhasil diupdate`);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating scheduler:', error);
      toast.error('Gagal menyimpan pengaturan');
    } finally {
      setSaving(prev => ({ ...prev, [schedulerName]: false }));
    }
  };

  const runSchedulerNow = async (schedulerName) => {
    try {
      // Use V2 MySQL endpoint
      const response = await axios.post(
        `${API_URL}/api/v2/system/schedulers/${schedulerName}/run`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`${schedulers[schedulerName]?.name || schedulerName} dijalankan`);
        fetchData(); // Refresh to show last_run time
      }
    } catch (error) {
      console.error('Error running scheduler:', error);
      toast.error('Gagal menjalankan scheduler');
    }
  };

  const getSchedulerIcon = (name) => {
    const icons = {
      invoice_reminder: Bell,
      auto_isolir: XCircle,
      router_heartbeat: Wifi,
      pppoe_sync: Activity,
      // legacy keys (kept for backward compat)
      ticket_reminders: Bell,
      wan_sync: Wifi,
      bandwidth_sync: Activity,
      invoice_generation: FileText,
    };
    return icons[name] || Clock;
  };

  const getSchedulerColor = (name) => {
    const colors = {
      invoice_reminder: 'from-orange-500 to-amber-500',
      auto_isolir: 'from-red-500 to-rose-500',
      router_heartbeat: 'from-blue-500 to-cyan-500',
      pppoe_sync: 'from-green-500 to-emerald-500',
      // legacy keys
      ticket_reminders: 'from-orange-500 to-amber-500',
      wan_sync: 'from-blue-500 to-cyan-500',
      bandwidth_sync: 'from-green-500 to-emerald-500',
      invoice_generation: 'from-purple-500 to-violet-500',
    };
    return colors[name] || 'from-gray-500 to-slate-500';
  };

  const getChangeTypeIcon = (type) => {
    const icons = {
      major: Rocket,
      feature: Sparkles,
      fix: Bug,
      improvement: Wrench
    };
    return icons[type] || Tag;
  };

  const getChangeTypeColor = (type) => {
    const colors = {
      major: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      feature: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      fix: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      improvement: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400';
  };

  const renderSchedulersTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Pengaturan Scheduler</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Kelola jadwal dan frekuensi background jobs</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <div className="grid gap-4">
        {Object.entries(schedulers).map(([key, scheduler]) => {
          const Icon = getSchedulerIcon(key);
          const gradientColor = getSchedulerColor(key);
          
          return (
            <Card key={key} className="border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="flex items-stretch">
                {/* Left color bar */}
                <div className={`w-2 bg-gradient-to-b ${gradientColor}`} />
                
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br ${gradientColor} shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                          {scheduler.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {scheduler.description}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Interval Setting */}
                      <div className="flex items-center gap-2">
                        {scheduler.interval_hours !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Setiap</span>
                            <Input
                              type="number"
                              min="1"
                              max="24"
                              value={scheduler.interval_hours}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 1;
                                setSchedulers(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], interval_hours: newValue }
                                }));
                              }}
                              className="w-16 h-8 text-center"
                            />
                            <span className="text-sm text-gray-500 dark:text-gray-400">jam</span>
                          </div>
                        )}
                        {scheduler.interval_minutes !== undefined && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Setiap</span>
                            <Input
                              type="number"
                              min="5"
                              max="120"
                              value={scheduler.interval_minutes}
                              onChange={(e) => {
                                const newValue = parseInt(e.target.value) || 5;
                                setSchedulers(prev => ({
                                  ...prev,
                                  [key]: { ...prev[key], interval_minutes: newValue }
                                }));
                              }}
                              className="w-16 h-8 text-center"
                            />
                            <span className="text-sm text-gray-500 dark:text-gray-400">menit</span>
                          </div>
                        )}
                        {scheduler.run_time && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Jam</span>
                            <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {scheduler.run_time}
                            </span>
                          </div>
                        )}
                        {scheduler.run_day && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">Tanggal</span>
                            <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                              {scheduler.run_day}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Enable/Disable Switch */}
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={scheduler.enabled}
                          onCheckedChange={(checked) => {
                            setSchedulers(prev => ({
                              ...prev,
                              [key]: { ...prev[key], enabled: checked }
                            }));
                          }}
                        />
                        <span className={`text-xs font-medium ${scheduler.enabled ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                          {scheduler.enabled ? 'Aktif' : 'Nonaktif'}
                        </span>
                      </div>

                      {/* Save Button */}
                      <Button
                        size="sm"
                        onClick={() => updateScheduler(key, {
                          enabled: scheduler.enabled,
                          interval_minutes: scheduler.interval_minutes,
                          interval_hours: scheduler.interval_hours
                        })}
                        disabled={saving[key]}
                        className="gap-1"
                      >
                        {saving[key] ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Save className="w-3 h-3" />
                        )}
                        Simpan
                      </Button>

                      {/* Run Now Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => runSchedulerNow(key)}
                        className="gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Run
                      </Button>
                    </div>
                  </div>

                  {/* Last Run Info */}
                  {scheduler.last_run && (
                    <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                      <Clock className="w-3 h-3 inline mr-1" />
                      Terakhir dijalankan: {new Date(scheduler.last_run).toLocaleString('id-ID')}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Tips Optimasi:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-400">
                <li>Kurangi frekuensi sync untuk hemat memory</li>
                <li>Nonaktifkan scheduler yang tidak diperlukan</li>
                <li>Gunakan &quot;Run&quot; untuk menjalankan manual saat diperlukan</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  const renderVersionTab = () => (
    <div className="space-y-6">
      {/* Current Version Card */}
      <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 dark:from-indigo-600 dark:to-purple-700 border-0 shadow-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <GitBranch className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-indigo-100 text-sm font-medium">Versi Aplikasi</p>
                <p className="text-3xl font-bold text-white">v{version}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-indigo-100 text-sm">ISP Billing System</p>
              <p className="text-white font-medium">RadiusLink</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Changelog */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
          <History className="w-5 h-5" />
          Riwayat Perubahan (Changelog)
        </h2>
        
        <div className="space-y-3">
          {changelog.map((release, index) => {
            const TypeIcon = getChangeTypeIcon(release.type);
            const isExpanded = expandedVersion === release.version;
            const isLatest = index === 0;
            
            return (
              <Card 
                key={release.version} 
                className={`border overflow-hidden transition-all duration-200 ${
                  isLatest 
                    ? 'border-indigo-300 dark:border-indigo-700 shadow-md' 
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div 
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedVersion(isExpanded ? null : release.version)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                      
                      <span className={`px-2.5 py-1 rounded-lg text-sm font-bold ${
                        isLatest 
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' 
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                      }`}>
                        v{release.version}
                      </span>
                      
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${getChangeTypeColor(release.type)}`}>
                        <TypeIcon className="w-3 h-3" />
                        {release.type === 'major' ? 'Major' : 
                         release.type === 'feature' ? 'Feature' :
                         release.type === 'fix' ? 'Bug Fix' : 'Improvement'}
                      </span>
                      
                      {isLatest && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          Latest
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      {new Date(release.date).toLocaleDateString('id-ID', { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      })}
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-800">
                    <ul className="space-y-2 mt-3">
                      {release.changes.map((change, idx) => (
                        <li 
                          key={idx} 
                          className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2"
                        >
                          <span className="text-gray-400 mt-1">•</span>
                          <span>{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderWebhookTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">PPPoE Webhook Settings</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Konfigurasi endpoint untuk menerima event PPPoE dari MikroTik</p>
        </div>
        <Button variant="outline" onClick={fetchWebhookSettings} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Production URL Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-500" />
            URL Production
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Masukkan URL production aplikasi Anda. URL ini akan digunakan di script MikroTik.
          </p>
          <Input
            placeholder="https://billing.domain-anda.com"
            value={webhookSettings.custom_webhook_url}
            onChange={(e) => setWebhookSettings(prev => ({ ...prev, custom_webhook_url: e.target.value }))}
          />
          <p className="text-xs text-gray-400">
            Kosongkan jika ingin menggunakan URL otomatis dari sistem.
          </p>
        </CardContent>
      </Card>

      {/* API Key Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Key className="w-5 h-5 text-amber-500" />
            API Key
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            API Key digunakan untuk autentikasi request dari MikroTik. Simpan key ini dengan aman.
          </p>
          
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={showApiKey ? "text" : "password"}
                value={webhookSettings.api_key || 'Belum di-generate'}
                readOnly
                className="pr-10 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              variant="outline"
              onClick={() => copyToClipboard(webhookSettings.api_key, 'API Key')}
              disabled={!webhookSettings.api_key}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button onClick={generateNewApiKey} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Generate Baru
            </Button>
          </div>
          
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Generate key baru akan membuat key lama tidak valid. Update script MikroTik dengan key baru.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* IP Whitelist Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            IP Whitelist
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktifkan IP Whitelist</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Hanya terima request dari IP yang terdaftar</p>
            </div>
            <Switch
              checked={webhookSettings.ip_whitelist_enabled}
              onCheckedChange={(checked) => setWebhookSettings(prev => ({ ...prev, ip_whitelist_enabled: checked }))}
            />
          </div>
          
          {webhookSettings.ip_whitelist_enabled && (
            <div className="space-y-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <div className="flex gap-2">
                <Input
                  placeholder="Masukkan IP (contoh: 192.168.1.1)"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addIpToWhitelist()}
                />
                <Button onClick={addIpToWhitelist} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Tambah
                </Button>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {webhookSettings.allowed_ips.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Belum ada IP yang ditambahkan</p>
                ) : (
                  webhookSettings.allowed_ips.map((ip) => (
                    <div key={ip} className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-full">
                      <Globe className="w-3 h-3 text-gray-500 dark:text-slate-400" />
                      <span className="text-sm font-mono">{ip}</span>
                      <button
                        onClick={() => removeIpFromWhitelist(ip)}
                        className="ml-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scheduler Fallback Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-green-500" />
            Scheduler Fallback
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Scheduler akan sync status pelanggan dari RADIUS/MikroTik sebagai fallback jika webhook tidak tersedia.
          </p>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Aktifkan Scheduler</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Sync otomatis status PPPoE</p>
            </div>
            <Switch
              checked={webhookSettings.scheduler_enabled}
              onCheckedChange={(checked) => setWebhookSettings(prev => ({ ...prev, scheduler_enabled: checked }))}
            />
          </div>
          
          {webhookSettings.scheduler_enabled && (
            <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
              <span className="text-sm text-gray-600 dark:text-gray-400">Interval:</span>
              <Input
                type="number"
                min="1"
                max="60"
                value={webhookSettings.scheduler_interval_minutes}
                onChange={(e) => setWebhookSettings(prev => ({ 
                  ...prev, 
                  scheduler_interval_minutes: parseInt(e.target.value) || 5 
                }))}
                className="w-20"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">menit</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveWebhookSettings} disabled={savingWebhook} className="gap-2">
          {savingWebhook ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Simpan Pengaturan
        </Button>
      </div>

      {/* MikroTik Script Template */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Code className="w-5 h-5 text-purple-500" />
            Template Script MikroTik
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Tambahkan script berikut ke PPPoE Server Profile di MikroTik Anda.
          </p>
          
          {mikrotikScript ? (
            <div className="space-y-4">
              {/* On-Up Script */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">On-Up Script (Connect)</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(mikrotikScript.on_up_script, 'On-Up Script')}
                    className="gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-48">
                  {mikrotikScript.on_up_script}
                </pre>
              </div>
              
              {/* On-Down Script */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">On-Down Script (Disconnect)</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(mikrotikScript.on_down_script, 'On-Down Script')}
                    className="gap-1"
                  >
                    <Copy className="w-3 h-3" />
                    Copy
                  </Button>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-48">
                  {mikrotikScript.on_down_script}
                </pre>
              </div>
              
              {/* API Endpoint Info */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  <Info className="w-3 h-3 inline mr-1" />
                  <strong>Endpoint:</strong> {mikrotikScript.api_endpoint}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <Code className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Generate API Key terlebih dahulu untuk melihat script</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderBillingTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Pengaturan Billing</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Konfigurasi perpanjangan tanggal jatuh tempo saat pembayaran</p>
        </div>
        <Button variant="outline" onClick={fetchBillingSettings} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Extend Method Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-green-500" />
            Metode Perpanjangan Tanggal Jatuh Tempo
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Pilih metode perhitungan tanggal jatuh tempo baru setelah pelanggan melakukan pembayaran.
          </p>
          
          <div className="space-y-3">
            {billingOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => setBillingSettings(prev => ({ ...prev, extend_method: option.value }))}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  billingSettings.extend_method === option.value
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    billingSettings.extend_method === option.value
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                  }`}>
                    {option.value === 'from_due_date' ? (
                      <Calendar className="w-5 h-5" />
                    ) : (
                      <DollarSign className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold ${
                        billingSettings.extend_method === option.value
                          ? 'text-green-700 dark:text-green-400'
                          : 'text-gray-800 dark:text-gray-100'
                      }`}>
                        {option.label}
                      </h3>
                      {billingSettings.extend_method === option.value && (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {option.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Example Box */}
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-medium mb-2">Contoh:</p>
                  {billingSettings.extend_method === 'from_due_date' ? (
                    <div className="text-blue-700 dark:text-blue-400 space-y-1">
                      <p>• Tanggal jatuh tempo: <strong>10 Januari 2025</strong></p>
                      <p>• Pelanggan bayar: <strong>15 Januari 2025</strong></p>
                      <p>• Tanggal jatuh tempo baru: <strong>10 Februari 2025</strong> (dari tanggal JT lama + 1 bulan)</p>
                    </div>
                  ) : (
                    <div className="text-blue-700 dark:text-blue-400 space-y-1">
                      <p>• Tanggal jatuh tempo: <strong>10 Januari 2025</strong></p>
                      <p>• Pelanggan bayar: <strong>15 Januari 2025</strong></p>
                      <p>• Tanggal jatuh tempo baru: <strong>15 Februari 2025</strong> (dari tanggal bayar + 1 bulan)</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {/* Pregen Window Section */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-indigo-500" />
            Penerbitan Tagihan Sebelum Jatuh Tempo (H-N)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Tagihan periode berikutnya otomatis diterbitkan dan bisa dibayar mulai <strong>N hari</strong> sebelum tanggal jatuh tempo (pola aplikasi lama). Contoh: isi <strong>5</strong> artinya tagihan baru muncul mulai H-5 sebelum jatuh tempo.
          </p>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">H-minus</span>
            <Input
              type="number"
              min={1}
              max={30}
              className="w-28"
              data-testid="input-pregen-days"
              value={billingSettings.invoice_pregen_days ?? 5}
              onChange={(e) => setBillingSettings(prev => ({ ...prev, invoice_pregen_days: parseInt(e.target.value || '5', 10) }))}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">hari sebelum jatuh tempo</span>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveBillingSettings} disabled={savingBilling} className="gap-2">
          {savingBilling ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Simpan Pengaturan
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 dark:border-indigo-400 mb-4"></div>
        <p className="text-gray-500 dark:text-gray-400">Memuat pengaturan sistem...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
          <Settings className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Pengaturan Sistem</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Kelola scheduler dan lihat informasi versi</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 dark:bg-gray-800 p-1 rounded-xl flex-wrap">
          <TabsTrigger 
            value="schedulers" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <Clock className="w-4 h-4" />
            Scheduler
          </TabsTrigger>
          <TabsTrigger 
            value="webhook" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <Wifi className="w-4 h-4" />
            PPPoE Webhook
          </TabsTrigger>
          <TabsTrigger 
            value="version" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <GitBranch className="w-4 h-4" />
            Version & Changelog
          </TabsTrigger>
          <TabsTrigger 
            value="billing" 
            className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-gray-700 data-[state=active]:shadow-sm rounded-lg"
          >
            <DollarSign className="w-4 h-4" />
            Billing
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="schedulers" className="mt-0">{renderSchedulersTab()}</TabsContent>
          <TabsContent value="webhook" className="mt-0">{renderWebhookTab()}</TabsContent>
          <TabsContent value="version" className="mt-0">{renderVersionTab()}</TabsContent>
          <TabsContent value="billing" className="mt-0">{renderBillingTab()}</TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default SystemSettings;
